import { useRef, useState } from 'react';
import { Download, Upload, X, AlertCircle } from 'lucide-react';

const ExpensesDataSheet = ({ open, storage, onClose }) => {
  const fileInputRef = useRef(null);
  const [phase, setPhase] = useState({ kind: 'idle' });
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await storage.exportData();
      const payload = { version: 1, exportedAt: new Date().toISOString(), ...data };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expenses-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result);
        if (
          !Array.isArray(parsed.expenses) ||
          parsed.income === null ||
          typeof parsed.income !== 'object'
        ) {
          setPhase({ kind: 'error', message: 'Invalid file — missing expenses or income data.' });
          return;
        }
        setPhase({
          kind: 'pending',
          fileName: file.name,
          expenseCount: parsed.expenses.length,
          data: { expenses: parsed.expenses, income: parsed.income },
        });
      } catch {
        setPhase({ kind: 'error', message: 'Could not parse the selected file.' });
      }
    };
    reader.readAsText(file);
  };

  const confirmImport = async () => {
    if (phase.kind !== 'pending') return;
    await storage.importData(phase.data);
    onClose();
    window.location.reload();
  };

  const handleClose = () => {
    setPhase({ kind: 'idle' });
    onClose();
  };

  return (
    <>
      <div
        onClick={handleClose}
        className={`fixed inset-0 bg-gray-900/40 z-[200] transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <div
        className={`fixed bottom-0 inset-x-0 max-w-lg mx-auto bg-white rounded-t-2xl shadow-xl z-[300] max-h-[92dvh] overflow-y-auto transition-transform duration-300 ease-out ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="w-9 h-1 rounded-full bg-gray-200 mx-auto sticky top-3 mt-3 mb-2" />
        <div className="px-5 pt-2 pb-8">

          {phase.kind === 'idle' && (
            <div className="space-y-1">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">Data</p>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4 text-gray-400" />
                {exporting ? 'Exporting…' : 'Export JSON'}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Upload className="w-4 h-4 text-gray-400" />
                Import JSON
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                onClick={handleClose}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          )}

          {phase.kind === 'pending' && (
            <div className="space-y-4">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Confirm import</p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl divide-y divide-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">File</span>
                  <span className="text-sm text-gray-900 truncate max-w-[60%]">{phase.fileName}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Expenses</span>
                  <span className="text-sm text-gray-900">{phase.expenseCount}</span>
                </div>
              </div>
              <p className="text-xs text-gray-400">This will replace all current expenses data.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setPhase({ kind: 'idle' })}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-500 text-sm hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmImport}
                  className="flex-1 px-4 py-2.5 bg-accent text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm"
                >
                  Confirm
                </button>
              </div>
            </div>
          )}

          {phase.kind === 'error' && (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-[11px] font-semibold text-negative uppercase tracking-wide">
                <AlertCircle className="w-3.5 h-3.5" />
                Error
              </p>
              <p className="text-sm text-gray-500">{phase.message}</p>
              <button
                onClick={() => setPhase({ kind: 'idle' })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-500 text-sm hover:text-gray-900 hover:bg-gray-50 transition-colors"
              >
                OK
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default ExpensesDataSheet;
